import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import PageLayout from "../components/PageLayout";
import { theme } from "../theme";
import { getSession } from "../auth";

// ── Config ────────────────────────────────────────────────────────────────────
const API_BASE = process.env.REACT_APP_API_URL || (
  window.location.hostname === "localhost"
    ? "http://localhost:8000"
    : "https://nexgen-backend-60om.onrender.com"
);
const WS_BASE  = API_BASE.replace(/^https/, "wss").replace(/^http/, "ws") + "/live/ws";
const LIVE_API = API_BASE + "/live";

function getWsUrl() {
  const { tenant_id } = getSession();
  return tenant_id ? `${WS_BASE}?tenant_id=${tenant_id}` : WS_BASE;
}

const MAX_EVENTS   = 500;
const MAX_TIMELINE = 60;

// ── Color palettes ────────────────────────────────────────────────────────────
const SEV_COLORS = {
  critical: "#FF0033", high: "#FF6B35",
  medium: "#FFB800",   low: "#00D4FF", info: "#A78BFA",
};
const CHART_COLORS = [
  "#FF0033","#FF6B35","#FFB800","#00D4FF",
  "#A78BFA","#00FF88","#FF00AA","#4488FF","#FF8C00","#00CCAA",
];
const PROTO_COLORS = {
  tcp: "#00D4FF", udp: "#FFB800", icmp: "#FF6B35",
  unknown: "#666", other: "#A78BFA",
};

// ── Tiny helpers ──────────────────────────────────────────────────────────────
const badge = (label, color, bg) => ({
  display: "inline-block", padding: "2px 8px", borderRadius: 4,
  fontSize: 10, fontWeight: 700, letterSpacing: 0.8,
  color: color || "#fff", background: bg || "rgba(255,255,255,0.1)",
  fontFamily: theme.fonts?.mono || "monospace",
});

const card = (extra = {}) => ({
  background: theme.colors?.card || "#1a1d2e",
  border: `1px solid ${theme.colors?.cardBorder || "#2a2d3e"}`,
  borderRadius: 10, padding: 14, ...extra,
});

const labelStyle = {
  fontSize: 10, fontWeight: 700, letterSpacing: 1.2,
  color: theme.colors?.textMuted || "#666",
  fontFamily: theme.fonts?.mono || "monospace",
  marginBottom: 6, textTransform: "uppercase",
};

function SevBadge({ sev }) {
  const c = SEV_COLORS[sev] || "#888";
  return <span style={badge(sev?.toUpperCase(), c, c + "22")}>{sev || "info"}</span>;
}

function Dot({ color }) {
  return (
    <span style={{
      display: "inline-block", width: 8, height: 8,
      borderRadius: "50%", background: color, marginRight: 6,
      boxShadow: `0 0 6px ${color}`,
    }} />
  );
}

// ── Custom tooltip for charts ─────────────────────────────────────────────────
function ChartTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "#0d0f1a", border: "1px solid #333", borderRadius: 6, padding: "8px 12px", fontSize: 11 }}>
      {label && <div style={{ color: "#888", marginBottom: 4 }}>{label}</div>}
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color || "#fff" }}>
          {p.name}: <b>{p.value}</b>
        </div>
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function LiveIDS() {
  const [connected, setConnected]   = useState(false);
  const [running, setRunning]       = useState(() => sessionStorage.getItem("lids_running") === "1");
  const [events, setEvents]         = useState(() => { try { return JSON.parse(sessionStorage.getItem("lids_events") || "[]"); } catch { return []; } });
  const [timeline, setTimeline]     = useState(() => { try { return JSON.parse(sessionStorage.getItem("lids_timeline") || "[]"); } catch { return []; } });
  const [status, setStatus]         = useState(null);
  const [filter, setFilter]         = useState("");
  const [paused, setPaused]         = useState(false);
  const [watchPath, setWatchPath]   = useState("");
  const [watchMsg, setWatchMsg]     = useState("");
  const [activeTab, setActiveTab]   = useState("charts");

  const wsRef         = useRef(null);
  const bufferRef     = useRef([]);
  const tickRef       = useRef(null);
  const tableRef      = useRef(null);
  const reconnectRef  = useRef(null);
  const manualStopRef = useRef(false);

  // ── WebSocket connection ──────────────────────────────────────────────────
  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    manualStopRef.current = false;
    const ws = new WebSocket(getWsUrl());
    wsRef.current = ws;

    ws.onopen = () => { setConnected(true); setRunning(true); };

    ws.onclose = () => {
      setConnected(false);
      if (!manualStopRef.current) {
        reconnectRef.current = setTimeout(() => connect(), 3000);
      }
    };

    ws.onerror = () => { setConnected(false); };

    ws.onmessage = (e) => {
      if (paused) return;
      try {
        const ev = JSON.parse(e.data);
        if (ev === "ping" || ev === "pong") return;
        bufferRef.current.push(ev);
      } catch (_) {}
    };
  }, [paused]);

  const disconnect = useCallback(() => {
    manualStopRef.current = true;
    clearTimeout(reconnectRef.current);
    wsRef.current?.close();
    wsRef.current = null;
    setConnected(false);
    setRunning(false);
    sessionStorage.removeItem("lids_running");
  }, []);

  // ── Persist session state across navigation ───────────────────────────────
  useEffect(() => {
    if (running) sessionStorage.setItem("lids_running", "1");
  }, [running]);

  useEffect(() => {
    sessionStorage.setItem("lids_events", JSON.stringify(events.slice(0, MAX_EVENTS)));
  }, [events]);

  useEffect(() => {
    sessionStorage.setItem("lids_timeline", JSON.stringify(timeline.slice(-MAX_TIMELINE)));
  }, [timeline]);

  // ── Auto-reconnect on mount if session was running ────────────────────────
  useEffect(() => {
    if (sessionStorage.getItem("lids_running") === "1") connect();
    return () => clearTimeout(reconnectRef.current);
  }, []); // eslint-disable-line

  // ── Flush buffer to state every 800ms ────────────────────────────────────
  useEffect(() => {
    tickRef.current = setInterval(() => {
      if (!bufferRef.current.length) return;
      const incoming = bufferRef.current.splice(0);
      setEvents(prev => {
        const combined = [...incoming, ...prev].slice(0, MAX_EVENTS);
        return combined;
      });
      // Update timeline
      const now = new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
      setTimeline(prev => {
        const next = [...prev, {
          t: now,
          total: incoming.length,
          anomaly: incoming.filter(e => e.if_anomaly).length,
          critical: incoming.filter(e => e.severity === "critical").length,
        }];
        return next.slice(-MAX_TIMELINE);
      });
    }, 800);
    return () => clearInterval(tickRef.current);
  }, []);

  // ── Poll /live/status ─────────────────────────────────────────────────────
  useEffect(() => {
    const poll = async () => {
      try {
        const r = await fetch(LIVE_API + "/status");
        if (r.ok) setStatus(await r.json());
      } catch (_) {}
    };
    poll();
    const id = setInterval(poll, 5000);
    return () => clearInterval(id);
  }, []);

  // ── Auto-scroll table ─────────────────────────────────────────────────────
  useEffect(() => {
    if (tableRef.current && !paused) {
      tableRef.current.scrollTop = 0;
    }
  }, [events, paused]);

  // ── Computed chart data ───────────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!filter) return events;
    const f = filter.toLowerCase();
    return events.filter(e =>
      (e.src_ip || "").includes(f) ||
      (e.dst_ip || "").includes(f) ||
      (e.attack_type || "").toLowerCase().includes(f) ||
      (e.vendor || "").toLowerCase().includes(f) ||
      String(e.dst_port || "").includes(f)
    );
  }, [events, filter]);

  const attackDist = useMemo(() => {
    const counts = {};
    filtered.forEach(e => {
      const k = e.rf_label || e.attack_type || "Unknown";
      counts[k] = (counts[k] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1]).slice(0, 8)
      .map(([name, value]) => ({ name, value }));
  }, [filtered]);

  const severityDist = useMemo(() => {
    const counts = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
    filtered.forEach(e => { if (counts[e.severity] !== undefined) counts[e.severity]++; });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [filtered]);

  const topIPs = useMemo(() => {
    const counts = {};
    filtered.forEach(e => { if (e.src_ip) counts[e.src_ip] = (counts[e.src_ip] || 0) + 1; });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 8)
      .map(([ip, count]) => ({ ip, count }));
  }, [filtered]);

  const portDist = useMemo(() => {
    const counts = {};
    filtered.forEach(e => {
      const p = String(e.dst_port || 0);
      if (p !== "0") counts[p] = (counts[p] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 10)
      .map(([port, count]) => ({ port, count }));
  }, [filtered]);

  const protoDist = useMemo(() => {
    const counts = {};
    filtered.forEach(e => {
      const p = (e.protocol || "unknown").toLowerCase();
      counts[p] = (counts[p] || 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [filtered]);

  const vendorSet = useMemo(() => {
    const s = new Set(filtered.map(e => e.vendor).filter(Boolean));
    return [...s].join(", ") || "—";
  }, [filtered]);

  // ── File watcher submit ───────────────────────────────────────────────────
  const startWatch = async () => {
    if (!watchPath) return;
    try {
      const r = await fetch(LIVE_API + "/watch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ filepath: watchPath }),
      });
      const d = await r.json();
      setWatchMsg(d.ok ? `✓ Watching: ${watchPath}` : `✗ ${d.error}`);
    } catch (e) {
      setWatchMsg("✗ Connection failed");
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const accentColor = theme.colors?.accent || "#00D4FF";
  const textPrimary = theme.colors?.textPrimary || "#e0e0e0";
  const textMuted   = theme.colors?.textMuted   || "#666";
  const cardBg      = theme.colors?.card        || "#1a1d2e";
  const borderColor = theme.colors?.cardBorder  || "#2a2d3e";

  return (
    <PageLayout title="Live IDS Feed">
      {/* ── Status bar ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        {/* Connection pill */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, background: connected ? "#00FF8811" : "#FF003311", border: `1px solid ${connected ? "#00FF8844" : "#FF003344"}`, borderRadius: 20, padding: "6px 14px" }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: connected ? "#00FF88" : "#FF0033", display: "inline-block", boxShadow: connected ? "0 0 8px #00FF88" : "none", animation: connected ? "pulse 1.5s infinite" : "none" }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: connected ? "#00FF88" : "#FF0033", fontFamily: theme.fonts?.mono || "monospace" }}>
            {connected ? "LIVE" : "OFFLINE"}
          </span>
        </div>

        {/* Event counter */}
        <div style={{ fontSize: 12, color: textMuted, fontFamily: theme.fonts?.mono || "monospace" }}>
          <span style={{ color: accentColor, fontWeight: 700 }}>{events.length.toLocaleString()}</span> events
          {status && <span style={{ marginLeft: 10 }}>· Backend processed: <span style={{ color: accentColor }}>{status.events_processed?.toLocaleString()}</span></span>}
        </div>

        {/* Vendor detected */}
        {vendorSet !== "—" && (
          <div style={{ fontSize: 11, color: textMuted, fontFamily: theme.fonts?.mono || "monospace" }}>
            Vendor: <span style={{ color: accentColor }}>{vendorSet}</span>
          </div>
        )}

        {/* ML status */}
        {status?.ml && (
          <div style={{ fontSize: 11, fontFamily: theme.fonts?.mono || "monospace", color: textMuted }}>
            IF: <span style={{ color: status.ml.if_ready ? "#00FF88" : "#FFB800" }}>{status.ml.if_ready ? "READY" : `${status.ml.if_buffer}/${100} warming`}</span>
            {" · "}RF: <span style={{ color: status.ml.rf_ready ? "#00FF88" : textMuted }}>{status.ml.rf_ready ? "READY" : "not trained"}</span>
          </div>
        )}

        {/* Controls */}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={() => { setFilter(""); setEvents([]); setTimeline([]); bufferRef.current = []; }}
            style={{ padding: "6px 14px", background: "rgba(255,255,255,0.05)", border: `1px solid ${borderColor}`, borderRadius: 6, color: textMuted, fontSize: 12, cursor: "pointer", fontFamily: theme.fonts?.mono || "monospace" }}>
            CLEAR
          </button>
          <button onClick={() => setPaused(p => !p)}
            style={{ padding: "6px 14px", background: paused ? "#FFB80022" : "rgba(255,255,255,0.05)", border: `1px solid ${paused ? "#FFB800" : borderColor}`, borderRadius: 6, color: paused ? "#FFB800" : textMuted, fontSize: 12, cursor: "pointer", fontFamily: theme.fonts?.mono || "monospace" }}>
            {paused ? "RESUME" : "PAUSE"}
          </button>
          <button onClick={running ? disconnect : connect}
            style={{ padding: "6px 18px", background: running ? "#FF003322" : "#00FF8822", border: `1px solid ${running ? "#FF0033" : "#00FF88"}`, borderRadius: 6, color: running ? "#FF0033" : "#00FF88", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: theme.fonts?.mono || "monospace" }}>
            {running ? "STOP" : "START"}
          </button>
        </div>
      </div>

      {/* ── Filter bar + File watcher ── */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <input
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Filter by IP, port, attack type, vendor…"
          style={{ flex: 1, minWidth: 240, padding: "8px 12px", background: cardBg, border: `1px solid ${borderColor}`, borderRadius: 6, color: textPrimary, fontSize: 12, fontFamily: theme.fonts?.mono || "monospace", outline: "none" }}
        />
        <input
          value={watchPath}
          onChange={e => setWatchPath(e.target.value)}
          placeholder="Tail file path (e.g. /var/log/suricata/eve.json)"
          style={{ flex: 1, minWidth: 300, padding: "8px 12px", background: cardBg, border: `1px solid ${borderColor}`, borderRadius: 6, color: textPrimary, fontSize: 12, fontFamily: theme.fonts?.mono || "monospace", outline: "none" }}
        />
        <button onClick={startWatch}
          style={{ padding: "8px 16px", background: "#A78BFA22", border: "1px solid #A78BFA55", borderRadius: 6, color: "#A78BFA", fontSize: 12, cursor: "pointer", fontFamily: theme.fonts?.mono || "monospace" }}>
          WATCH FILE
        </button>
        {watchMsg && <span style={{ fontSize: 11, color: watchMsg.startsWith("✓") ? "#00FF88" : "#FF0033", alignSelf: "center", fontFamily: theme.fonts?.mono || "monospace" }}>{watchMsg}</span>}
      </div>

      {/* ── Tab selector ── */}
      <div style={{ display: "flex", gap: 4, marginBottom: 14 }}>
        {["charts", "table"].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            style={{ padding: "6px 18px", background: activeTab === tab ? accentColor + "22" : "transparent", border: `1px solid ${activeTab === tab ? accentColor : borderColor}`, borderRadius: 6, color: activeTab === tab ? accentColor : textMuted, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: theme.fonts?.mono || "monospace", letterSpacing: 1, textTransform: "uppercase" }}>
            {tab}
          </button>
        ))}
      </div>

      {activeTab === "charts" ? (
        <>
          {/* ── Row 1: Attack types + Severity timeline ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 12, marginBottom: 12 }}>
            {/* Attack type pie */}
            <div style={card()}>
              <div style={labelStyle}>Attack Types {filtered.length > 0 && `(${attackDist.length} types)`}</div>
              {attackDist.length === 0
                ? <Empty />
                : <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={attackDist} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} label={({ name, percent }) => percent > 0.05 ? name.slice(0, 12) : ""} labelLine={false}>
                        {attackDist.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Pie>
                      <Tooltip content={<ChartTip />} />
                    </PieChart>
                  </ResponsiveContainer>
              }
            </div>

            {/* Severity timeline */}
            <div style={card()}>
              <div style={labelStyle}>Severity Timeline — events per tick</div>
              {timeline.length === 0
                ? <Empty />
                : <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={timeline}>
                      <XAxis dataKey="t" tick={{ fontSize: 9, fill: textMuted }} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 9, fill: textMuted }} />
                      <Tooltip content={<ChartTip />} />
                      <Line type="monotone" dataKey="total"   stroke={accentColor}    strokeWidth={2} dot={false} name="total" />
                      <Line type="monotone" dataKey="anomaly" stroke="#FF6B35"         strokeWidth={1.5} dot={false} name="anomaly" strokeDasharray="4 2" />
                      <Line type="monotone" dataKey="critical" stroke={SEV_COLORS.critical} strokeWidth={1} dot={false} name="critical" strokeDasharray="2 2" />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                    </LineChart>
                  </ResponsiveContainer>
              }
            </div>
          </div>

          {/* ── Row 2: Top IPs + Port distribution ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            {/* Top source IPs */}
            <div style={card()}>
              <div style={labelStyle}>Top Source IPs</div>
              {topIPs.length === 0
                ? <Empty />
                : <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={topIPs} layout="vertical">
                      <XAxis type="number" tick={{ fontSize: 9, fill: textMuted }} />
                      <YAxis dataKey="ip" type="category" tick={{ fontSize: 9, fill: textMuted }} width={100} />
                      <Tooltip content={<ChartTip />} />
                      <Bar dataKey="count" name="events" radius={[0, 4, 4, 0]}>
                        {topIPs.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
              }
            </div>

            {/* Port distribution */}
            <div style={card()}>
              <div style={labelStyle}>Top Destination Ports</div>
              {portDist.length === 0
                ? <Empty />
                : <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={portDist}>
                      <XAxis dataKey="port" tick={{ fontSize: 9, fill: textMuted }} />
                      <YAxis tick={{ fontSize: 9, fill: textMuted }} />
                      <Tooltip content={<ChartTip />} />
                      <Bar dataKey="count" name="events" radius={[4, 4, 0, 0]}>
                        {portDist.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
              }
            </div>
          </div>

          {/* ── Row 3: Protocol donut + Severity breakdown ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            {/* Protocol split */}
            <div style={card()}>
              <div style={labelStyle}>Protocol Distribution</div>
              {protoDist.length === 0
                ? <Empty />
                : <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={protoDist} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={75} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                        {protoDist.map((e, i) => <Cell key={i} fill={PROTO_COLORS[e.name] || CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Pie>
                      <Tooltip content={<ChartTip />} />
                    </PieChart>
                  </ResponsiveContainer>
              }
            </div>

            {/* Severity bar */}
            <div style={card()}>
              <div style={labelStyle}>Severity Breakdown</div>
              {severityDist.every(s => s.value === 0)
                ? <Empty />
                : <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={severityDist}>
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: textMuted }} />
                      <YAxis tick={{ fontSize: 9, fill: textMuted }} />
                      <Tooltip content={<ChartTip />} />
                      <Bar dataKey="value" name="events" radius={[4, 4, 0, 0]}>
                        {severityDist.map((e, i) => <Cell key={i} fill={SEV_COLORS[e.name] || "#888"} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
              }
            </div>
          </div>
        </>
      ) : null}

      {/* ── Live events table ── */}
      <div style={card({ padding: 0, overflow: "hidden" })}>
        <div style={{ padding: "10px 14px", borderBottom: `1px solid ${borderColor}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={labelStyle}>Live Event Stream — {filtered.length.toLocaleString()} events {paused && <span style={{ color: "#FFB800" }}>(PAUSED)</span>}</div>
          {filtered.length > 0 && (
            <span style={{ fontSize: 10, color: textMuted, fontFamily: theme.fonts?.mono || "monospace" }}>newest first</span>
          )}
        </div>
        <div ref={tableRef} style={{ maxHeight: 380, overflowY: "auto" }}>
          {filtered.length === 0
            ? <div style={{ padding: 32, textAlign: "center", color: textMuted, fontSize: 13 }}>
                {running ? "Waiting for data from IDS device…" : "Press START to begin receiving IDS data"}
              </div>
            : <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, fontFamily: theme.fonts?.mono || "monospace" }}>
                <thead>
                  <tr style={{ background: "rgba(255,255,255,0.03)", position: "sticky", top: 0 }}>
                    {["Time","Src IP","Dst IP","Port","Proto","Bytes","Severity","Attack / Label","Vendor","IF","RF"].map(h => (
                      <th key={h} style={{ padding: "7px 10px", textAlign: "left", color: textMuted, fontWeight: 700, fontSize: 9, letterSpacing: 1, whiteSpace: "nowrap", borderBottom: `1px solid ${borderColor}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.slice(0, 200).map((ev, i) => (
                    <tr key={ev.id ?? i} style={{ background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)", borderBottom: `1px solid ${borderColor}22` }}>
                      <td style={{ padding: "5px 10px", color: textMuted, whiteSpace: "nowrap" }}>{ev.timestamp?.slice(11, 23) || "—"}</td>
                      <td style={{ padding: "5px 10px", color: textPrimary }}>{ev.src_ip || "—"}</td>
                      <td style={{ padding: "5px 10px", color: textPrimary }}>{ev.dst_ip || "—"}</td>
                      <td style={{ padding: "5px 10px", color: "#FFB800" }}>{ev.dst_port || "—"}</td>
                      <td style={{ padding: "5px 10px", color: PROTO_COLORS[(ev.protocol || "").toLowerCase()] || textMuted }}>{(ev.protocol || "—").toUpperCase()}</td>
                      <td style={{ padding: "5px 10px", color: textMuted }}>{ev.bytes ? Number(ev.bytes).toLocaleString() : "—"}</td>
                      <td style={{ padding: "5px 10px" }}><SevBadge sev={ev.severity} /></td>
                      <td style={{ padding: "5px 10px", color: ev.rf_label && ev.rf_label !== "BENIGN" ? SEV_COLORS.high : textPrimary, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {ev.rf_label || ev.attack_type || "—"}
                      </td>
                      <td style={{ padding: "5px 10px", color: textMuted }}>{ev.vendor || "—"}</td>
                      <td style={{ padding: "5px 10px", color: ev.if_anomaly ? SEV_COLORS.critical : "#00FF88", fontWeight: 700 }}>
                        {ev.if_score !== null && ev.if_score !== undefined ? (ev.if_anomaly ? "⚠ ANOM" : "✓ OK") : "…"}
                      </td>
                      <td style={{ padding: "5px 10px", color: ev.rf_confidence > 0.8 ? "#00FF88" : textMuted }}>
                        {ev.rf_label ? `${(ev.rf_confidence * 100).toFixed(0)}%` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
          }
        </div>
      </div>

      {/* ── Connection instructions ── */}
      {!connected && (
        <div style={{ ...card({ marginTop: 12 }), borderColor: "#00D4FF33" }}>
          <div style={{ ...labelStyle, color: accentColor }}>How to Connect Your IDS Device</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
            {[
              { title: "HTTP POST (recommended)", color: "#00FF88", steps: [`POST ${API_BASE}/live/ingest`, `Body: {"events":[{...row...}]}`, "Supports: all vendors, JSON", "Port: same as platform"] },
              { title: "Syslog UDP/TCP", color: "#00D4FF", steps: ["Point syslog output to:", `${window.location.hostname}:5140`, "Formats: CEF, Fortinet, Cisco", "Syslog port 5140 (not 514)"] },
              { title: "File Tail", color: "#A78BFA", steps: ["Enter log file path above", "Click WATCH FILE", "Works with: Zeek conn.log,", "Suricata eve.json, any CSV"] },
            ].map(({ title, color, steps }) => (
              <div key={title} style={{ padding: 12, background: color + "08", border: `1px solid ${color}22`, borderRadius: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color, marginBottom: 8, fontFamily: theme.fonts?.mono || "monospace" }}>{title}</div>
                {steps.map((s, i) => <div key={i} style={{ fontSize: 10, color: textMuted, marginBottom: 3, fontFamily: theme.fonts?.mono || "monospace" }}>{s}</div>)}
              </div>
            ))}
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </PageLayout>
  );
}

function Empty() {
  const textMuted = theme.colors?.textMuted || "#666";
  return (
    <div style={{ height: 180, display: "flex", alignItems: "center", justifyContent: "center", color: textMuted, fontSize: 12 }}>
      No data yet
    </div>
  );
}
